import React, { createContext, useReducer, useCallback, useMemo } from "react";
import { generateToastId } from "../utils/toast/toastHelpers";

// Create the context
const ToastContext = createContext(undefined);

// Initial state
const initialState = {
  toasts: [],
  maxToasts: 5,
};

// Action types
const ADD_TOAST = "ADD_TOAST";
const REMOVE_TOAST = "REMOVE_TOAST";
const UPDATE_TOAST = "UPDATE_TOAST";
const CLEAR_ALL = "CLEAR_ALL";

// Reducer function
const toastReducer = (state, action) => {
  switch (action.type) {
    case ADD_TOAST: {
      const newToast = action.payload;

      // If we're at max capacity, remove the oldest toast
      if (state.toasts.length >= state.maxToasts) {
        const toastsWithoutOldest = state.toasts.slice(1);
        return {
          ...state,
          toasts: [...toastsWithoutOldest, newToast],
        };
      }

      return {
        ...state,
        toasts: [...state.toasts, newToast],
      };
    }

    case REMOVE_TOAST: {
      return {
        ...state,
        toasts: state.toasts.filter((toast) => toast.id !== action.payload),
      };
    }

    case UPDATE_TOAST: {
      return {
        ...state,
        toasts: state.toasts.map((toast) =>
          toast.id === action.payload.id
            ? { ...toast, ...action.payload.updates }
            : toast
        ),
      };
    }

    case CLEAR_ALL: {
      return {
        ...state,
        toasts: [],
      };
    }

    default:
      return state;
  }
};

// Provider component
export const ToastProvider = ({ children }) => {
  const [state, dispatch] = useReducer(toastReducer, initialState);

  // Add a new toast
  const addToast = useCallback((toast) => {
    const id = toast.id || generateToastId();
    const newToast = {
      ...toast,
      id,
      createdAt: Date.now(),
    };

    dispatch({ type: ADD_TOAST, payload: newToast });
    return id;
  }, []);

  // Remove a specific toast
  const removeToast = useCallback((id) => {
    dispatch({ type: REMOVE_TOAST, payload: id });
  }, []);

  // Update a specific toast
  const updateToast = useCallback((id, updates) => {
    dispatch({ type: UPDATE_TOAST, payload: { id, updates } });
  }, []);

  // Clear all toasts
  const clearAll = useCallback(() => {
    dispatch({ type: CLEAR_ALL });
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      toasts: state.toasts,
      addToast,
      removeToast,
      updateToast,
      clearAll,
    }),
    [state.toasts, addToast, removeToast, updateToast, clearAll]
  );

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
};

export default ToastContext;
